import { useEffect } from "react";
import { useSiteSettings } from "@/features/site-settings/hooks";

interface SEOProps {
  title?: string | null;
  description?: string | null;
  keywords?: string | null;
  ogTitle?: string | null;
  ogDescription?: string | null;
  ogImage?: string | null;
  canonicalUrl?: string | null;
  robotsIndex?: boolean;
  robotsFollow?: boolean;
}

export function SEO({
  title,
  description,
  keywords,
  ogTitle,
  ogDescription,
  ogImage,
  canonicalUrl,
  robotsIndex,
  robotsFollow,
}: SEOProps) {
  const siteSettings = useSiteSettings();
  const settings = siteSettings.data;

  useEffect(() => {
    const siteName = settings?.siteName ?? "Kalaco's Liveries";
    const finalTitle = title || settings?.seoTitle || siteName;
    const finalDescription = description || settings?.metaDescription || settings?.tagline || "";
    const finalKeywords = keywords || settings?.seoKeywords || "";
    const finalOgTitle = ogTitle || settings?.ogTitle || finalTitle;
    const finalOgDescription = ogDescription || settings?.ogDescription || finalDescription;
    const finalOgImage = ogImage || settings?.ogImage || settings?.logoUrl || "";
    const finalCanonical = canonicalUrl || settings?.canonicalUrl || window.location.href;
    const index = robotsIndex ?? settings?.robotsIndex ?? true;
    const follow = robotsFollow ?? settings?.robotsFollow ?? true;

    document.title = finalTitle === siteName ? siteName : `${finalTitle} | ${siteName}`;
    setMeta("description", finalDescription);
    setMeta("keywords", finalKeywords);
    setMeta("robots", `${index ? "index" : "noindex"},${follow ? "follow" : "nofollow"}`);
    setProperty("og:title", finalOgTitle);
    setProperty("og:description", finalOgDescription);
    setProperty("og:type", "website");
    setProperty("og:url", finalCanonical);
    setProperty("og:image", finalOgImage);
    setMeta("twitter:card", finalOgImage ? "summary_large_image" : "summary");
    setMeta("twitter:title", finalOgTitle);
    setMeta("twitter:description", finalOgDescription);
    setMeta("twitter:image", finalOgImage);
    setCanonical(finalCanonical);
  }, [canonicalUrl, description, keywords, ogDescription, ogImage, ogTitle, robotsFollow, robotsIndex, settings, title]);

  return null;
}

function setMeta(name: string, content: string) {
  setTag("meta", "name", name, content);
}

function setProperty(property: string, content: string) {
  setTag("meta", "property", property, content);
}

function setTag(tag: "meta", attr: "name" | "property", key: string, content: string) {
  let element = document.head.querySelector(`${tag}[${attr}="${key}"]`) as HTMLMetaElement | null;
  if (!element) {
    element = document.createElement(tag);
    element.setAttribute(attr, key);
    document.head.appendChild(element);
  }
  if (content) element.setAttribute("content", content);
  else element.removeAttribute("content");
}

function setCanonical(href: string) {
  let link = document.head.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
  if (!link) {
    link = document.createElement("link");
    link.rel = "canonical";
    document.head.appendChild(link);
  }
  link.href = href;
}
